import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  collection, query, where, onSnapshot, getDocs, getDoc,
  doc, setDoc, deleteDoc, writeBatch,
} from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase';
import { isAdminRole } from '../roles';
import { audienceFor as audienceForSids, fanOutRows } from '../noticeFanOut';
import { useModuleAdmin } from '../useModuleAdmin';
import NoticeTemplates from './NoticeTemplates';
import NoticeApprovalSettings from './NoticeApprovalSettings';
import { fillTemplate, noticeValues } from '../noticeTemplate';
import {
  STATUS, STATUS_LABEL, STATUS_COLOUR, stageFor, firstPendingStatus, logEntry,
} from '../noticeApproval';
import { useT } from '../i18n.jsx';
import {
  ClipboardList, FileText, Settings as SettingsIcon, Plus, Send, X, Search,
  Paperclip, CheckCircle2, Download, ShieldCheck, Lock, Trash2, ChevronDown, ChevronRight, CreditCard,
  Pencil,
} from 'lucide-react';
import Papa from 'papaparse';
import HeaderActionsPortal from './HeaderActionsPortal';
import {
  filterRecords, yearOptions, initialYear, noticeText, paymentText,
} from '../noticeFilter';
import '../theme-palette.css';

const noticeId = () => `PN-${Date.now().toString().slice(-6)}-${Math.floor(Math.random() * 10000)}`;
const paymentId = () => `PAY-${Date.now().toString().slice(-6)}-${Math.floor(Math.random() * 10000)}`;
const paymentDocId = (pid, sid) => `${pid}__${sid}`;

// Money is stored as integer cents, never as a float: 0.1 + 0.2 is not 0.3, and a rounding error in
// a fee collection is the kind of bug nobody notices until the accounts fail to balance.
// Both real-world routes go through K-Pay: paying online, or topping up an Octopus card with cash
// and tapping it at the school terminal. Either way K-Pay is the system of record, so the app must
// never be the thing that decides a fee was paid. SCHOOL_OVERRIDE exists only for exceptions
// (a terminal outage, cash taken at the office) and is deliberately labelled as such in the audit
// trail so it can be told apart from a settlement K-Pay actually confirmed.
const SETTLED_VIA = {
  KPAY_ONLINE: 'kpay_online',
  KPAY_OCTOPUS: 'kpay_octopus',
  SCHOOL_OVERRIDE: 'school_override',
};

const SETTLED_LABEL = {
  [SETTLED_VIA.KPAY_ONLINE]: 'K-Pay (online)',
  [SETTLED_VIA.KPAY_OCTOPUS]: 'K-Pay (Octopus at school)',
  [SETTLED_VIA.SCHOOL_OVERRIDE]: 'Recorded by school',
};

const toCents = (v) => Math.round((parseFloat(v) || 0) * 100);
const money = (cents) => (Number(cents || 0) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const signatureDocId = (nid, sid) => `${nid}__${sid}`;

const formatWhen = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return `${d.toLocaleDateString('en-CA')} ${d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
};

// Table chrome. Static, so it lives out here rather than being rebuilt on every render -- and so
// the roster below can use the same styles without them being threaded through as props.
const th = { padding: '0.65rem 0.75rem', fontWeight: '600', whiteSpace: 'nowrap' };
const td = { padding: '0.5rem 0.75rem', borderTop: '1px solid var(--color-border)', whiteSpace: 'nowrap' };
const headRow = { backgroundColor: 'var(--color-bg-app)', borderBottom: '1px solid var(--color-border)', fontSize: '0.8rem', color: 'var(--color-text-muted)', textAlign: 'left' };

// The recipient list scrolls inside its own card instead of running the length of the page. A
// notice targeting a whole form is taller than the window on its own, so without a cap the act of
// expanding one notice pushes every other notice past the fold -- with a dozen of them the list
// stops being navigable, which is exactly the state this screen is meant to give you.
const listScroller = { overflowX: 'auto', overflowY: 'auto', maxHeight: '45vh' };
// Sticky lives on the cells, not the row: a `tr` is not a containing block for sticky, so putting
// it on headRow scrolls the header away and leaves a column of bare names under nothing. The
// background has to be repeated here too, otherwise rows show through the header as it sticks.
const stickyTh = { ...th, position: 'sticky', top: 0, zIndex: 1, backgroundColor: 'var(--color-bg-app)', boxShadow: 'inset 0 -1px 0 var(--color-border)' };

const ROSTER_ALL = '__all__';

const chipStyle = (on) => ({
  padding: '0.28rem 0.7rem',
  borderRadius: 'var(--radius-md)',
  fontSize: '0.8rem',
  fontWeight: on ? '700' : '500',
  whiteSpace: 'nowrap',
  cursor: 'pointer',
  // The strip scrolls sideways rather than wrapping, so without this the chips are shrinkable flex
  // items and a form with eight classes squeezes every label instead of overflowing.
  flexShrink: 0,
  // Gold is the platform's "selected". Deliberately not a status colour: which class you happen to
  // be looking at is not a success, a warning or an error.
  backgroundColor: on ? 'var(--color-nav-active)' : 'transparent',
  border: '1px solid ' + (on ? 'var(--color-accent)' : 'var(--color-border)'),
  color: on ? 'var(--color-text-main)' : 'var(--color-text-subtle)',
});

// One horizontal strip per level of grouping. It scrolls sideways rather than wrapping, so the row
// beneath it does not move as the number of classes changes.
const ChipRow = ({ items, active, onPick }) => (
  <div style={{ display: 'flex', gap: '0.35rem', overflowX: 'auto', flexWrap: 'nowrap', padding: '0.1rem' }}>
    {items.map(it => (
      <button key={it.key} onClick={() => onPick(it.key)} style={chipStyle(it.key === active)}>
        {it.label}
        <span style={{ marginLeft: '0.35rem', opacity: 0.75, fontWeight: '500' }}>{it.count}</span>
      </button>
    ))}
  </div>
);

// A whole-form notice is 150 names, and whoever is clearing it works through one class at a time.
// Split it two ways -- level, then class -- so what is on screen is about forty rows rather than an
// entire year group.
//
// The selection is derived, not stored-and-corrected: a class leaves the strip the moment its last
// outstanding student is marked signed, and deriving means the view falls through to the next class
// on its own instead of showing an empty table until an effect catches up.
// Outstanding first, then by class, then by class number. Two of those three were missing: the
// rows arrived in whatever order Firestore returned them, so a class read 21, 1, 2, 3, 5 -- which is
// unusable for the one job this table has, which is working down a class list against a pile of
// paper slips.
const byStateThenClass = (a, b) => (
  (a.done === b.done ? 0 : (a.done ? 1 : -1))
  || (a.currentClass || '').localeCompare(b.currentClass || '', undefined, { numeric: true })
  || Number(a.classNumber || 0) - Number(b.classNumber || 0)
);

const StudentRoster = ({ rows, levelFor, levelLabel, actionLabel, actionTitle, undoLabel, doneLabel, pendingLabel, onAction }) => {
  const { t } = useT();
  const [level, setLevel] = useState(null);
  const [cls, setCls] = useState(null);

  const byLevel = useMemo(() => {
    const classes = new Map();
    // Sorted before grouping, so each class list inherits the order rather than needing its own pass.
    [...rows].sort(byStateThenClass).forEach(r => {
      const c = r.currentClass || '—';
      if (!classes.has(c)) classes.set(c, []);
      classes.get(c).push(r);
    });
    const levels = new Map();
    [...classes.entries()]
      .sort((a, b) => a[0].localeCompare(b[0], undefined, { numeric: true }))
      .forEach(([c, list]) => {
        const lv = levelFor(c);
        if (!levels.has(lv)) levels.set(lv, []);
        levels.get(lv).push([c, list]);
      });
    return [...levels.entries()].sort((a, b) => a[0].localeCompare(b[0], undefined, { numeric: true }));
  }, [rows, levelFor]);

  const activeLevel = byLevel.some(([lv]) => lv === level) ? level : byLevel[0]?.[0];
  const classesHere = byLevel.find(([lv]) => lv === activeLevel)?.[1] || [];
  const activeClass = cls === ROSTER_ALL || classesHere.some(([c]) => c === cls)
    ? cls
    : classesHere[0]?.[0];

  const visible = activeClass === ROSTER_ALL
    ? classesHere.flatMap(([, list]) => list)
    : (classesHere.find(([c]) => c === activeClass)?.[1] || []);

  // Nothing to choose between: one class would render a strip of a single button, and a control
  // with one option is furniture rather than navigation.
  const showLevels = byLevel.length > 1;
  const showClasses = classesHere.length > 1;

  return (
    <>
      {(showLevels || showClasses) && (
        <div style={{ padding: '0.6rem 0.75rem', borderTop: '1px solid var(--color-border)', backgroundColor: 'var(--color-bg-app)', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
          {showLevels && (
            <ChipRow
              items={byLevel.map(([lv, cs]) => ({
                key: lv,
                label: levelLabel(lv),
                count: cs.reduce((sum, [, list]) => sum + list.filter(r => !r.done).length, 0),
              }))}
              active={activeLevel}
              onPick={setLevel}
            />
          )}
          {showClasses && (
            <ChipRow
              items={[
                ...classesHere.map(([c, list]) => ({ key: c, label: c, count: list.filter(r => !r.done).length })),
                { key: ROSTER_ALL, label: t('parentNotice.all'), count: classesHere.reduce((sum, [, list]) => sum + list.filter(r => !r.done).length, 0) },
              ]}
              active={activeClass}
              onPick={setCls}
            />
          )}
        </div>
      )}

      <div style={listScroller}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
          <thead>
            <tr style={headRow}>
              <th style={stickyTh}>{t('parentNotice.className')}</th>
              <th style={stickyTh}>{t('parentNotice.classNumber')}</th>
              <th style={stickyTh}>{t('parentNotice.sid')}</th>
              <th style={stickyTh}>{t('parentNotice.name')}</th>
              <th style={stickyTh}>{t('parentNotice.status_')}</th>
              <th style={{ ...stickyTh, textAlign: 'right' }}>{t('parentNotice.action')}</th>
            </tr>
          </thead>
          <tbody>
            {visible.map(st => (
              <tr key={st.sid}>
                <td style={td}>{st.currentClass || '—'}</td>
                <td style={td}>{st.classNumber || '—'}</td>
                <td style={{ ...td, fontFamily: 'monospace' }}>{st.sid}</td>
                <td style={td}>{st.name}</td>
                <td style={td}>
                  <span style={{ fontSize: '0.72rem', fontWeight: '700', whiteSpace: 'nowrap', color: st.done ? 'var(--color-success-text)' : 'var(--color-warning-text)' }}>
                    {st.done ? doneLabel : pendingLabel}
                  </span>
                </td>
                <td style={{ ...td, textAlign: 'right' }}>
                  {/* Both actions are toggles underneath, so a row that is already done offers the
                      way back. A mis-click that removes the row from the list cannot be undone by
                      someone who can no longer see it. */}
                  <button className="btn-secondary" onClick={() => onAction(st)} title={st.done ? undefined : actionTitle} style={{ width: 'auto', padding: '0.25rem 0.7rem', fontSize: '0.8rem' }}>
                    {st.done ? undoLabel : actionLabel}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

// --- Audience picker ---------------------------------------------------------------------------
// Choosing who a notice goes to used to be one flat list of every student in the school with a
// checkbox beside each. That works for "these three students" and for nothing else: sending to a
// whole form meant hunting down 120 boxes, and once they were ticked there was no way to see what
// you had built other than counting.
//
// Two changes. Adding is by scope -- whole school, a level, a class, one student -- and what you
// have added is shown back to you as a short list you can open up.
//
// The summary is DERIVED from the chosen SIDs rather than stored as a record of what was clicked.
// That matters on edit: reopening an existing notice gives you its recipient list and nothing about
// how it was assembled, and a stored intent could disagree with the list it claims to describe. A
// derived summary cannot -- if it says "6D", every student in 6D is in the list.
// Labelled at render, because a module-level constant is built before there is a language.
const SCOPES = ['school', 'level', 'class', 'student'];

const AudiencePicker = ({ students, classOptions, levelFor, levelLabel, value, onChange, disabled }) => {
  const { t } = useT();
  const [scope, setScope] = useState('class');
  // What is ticked but not yet added. A Set of level codes, class codes or SIDs depending on scope
  // -- one at a time was the wrong shape: sending to four classes meant four rounds of the same
  // three clicks.
  const [sel, setSel] = useState(() => new Set());
  const [search, setSearch] = useState('');
  const [classFilter, setClassFilter] = useState('');
  const [openRow, setOpenRow] = useState(null);

  const switchScope = (id) => { setScope(id); setSel(new Set()); setSearch(''); setClassFilter(''); };
  const toggleSel = (key) => setSel(prev => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const chosen = useMemo(() => new Set(value), [value]);

  const levelOptions = useMemo(() => {
    const seen = new Map();
    classOptions.forEach(c => {
      const lv = levelFor(c);
      if (!seen.has(lv)) seen.set(lv, levelLabel(lv));
    });
    return [...seen.entries()].sort((a, b) => a[0].localeCompare(b[0], undefined, { numeric: true }));
  }, [classOptions, levelFor, levelLabel]);

  // Picking people one by one is nearly always "these four in 3B", so the class filter comes first
  // and the search is for the case where you know a name but not a class.
  const matches = useMemo(() => {
    const q = search.trim().toLowerCase();
    return students
      .filter(st => !classFilter || (st.currentClass || '') === classFilter)
      .filter(st => !q || `${st.name} ${st.sid} ${st.currentClass} ${st.classNumber} ${st.chineseName || ''}`.toLowerCase().includes(q))
      .sort((a, b) => (a.currentClass || '').localeCompare(b.currentClass || '', undefined, { numeric: true }) || Number(a.classNumber || 0) - Number(b.classNumber || 0));
  }, [students, search, classFilter]);

  const summary = useMemo(() => {
    const rows = [];
    const known = new Set(students.map(st => st.sid));

    if (students.length > 0 && students.every(st => chosen.has(st.sid))) {
      rows.push({ key: 'school', kind: t('parentNotice.wholeSchool'), label: t('parentNotice.everyStudent'), members: students });
    } else {
      const classes = new Map();
      students.forEach(st => {
        const c = st.currentClass || '—';
        if (!classes.has(c)) classes.set(c, []);
        classes.get(c).push(st);
      });

      const levels = new Map();
      [...classes.keys()].forEach(c => {
        const lv = levelFor(c);
        if (!levels.has(lv)) levels.set(lv, []);
        levels.get(lv).push(c);
      });

      const covered = new Set();
      [...levels.entries()]
        .sort((a, b) => a[0].localeCompare(b[0], undefined, { numeric: true }))
        .forEach(([lv, keys]) => {
          const members = keys.flatMap(c => classes.get(c));
          if (members.length > 0 && members.every(st => chosen.has(st.sid))) {
            rows.push({ key: 'lv:' + lv, kind: t('parentNotice.scopes.level'), label: levelLabel(lv), members });
            keys.forEach(c => covered.add(c));
          }
        });

      [...classes.entries()]
        .sort((a, b) => a[0].localeCompare(b[0], undefined, { numeric: true }))
        .forEach(([c, members]) => {
          if (covered.has(c)) return;
          const picked = members.filter(st => chosen.has(st.sid));
          if (picked.length === 0) return;
          if (picked.length === members.length) {
            rows.push({ key: 'cls:' + c, kind: t('parentNotice.scopes.class'), label: c, members });
          } else {
            // A part-selected class is listed person by person rather than as "6D (4 of 27)" --
            // that reads like the class is included when most of it is not.
            picked.forEach(st => rows.push({
              key: 'sid:' + st.sid,
              kind: t('parentNotice.scopes.student'),
              label: `${st.currentClass || '—'} (${st.classNumber || '—'}) ${st.name}`,
              members: [st],
            }));
          }
        });
    }

    // Recipients who are no longer on the roll. Said out loud rather than quietly dropped: the count
    // on the button would otherwise disagree with the list underneath it, and nobody would know why.
    const orphans = [...chosen].filter(sid => !known.has(sid));
    if (orphans.length > 0) {
      rows.push({
        key: 'orphans',
        kind: t('parentNotice.notOnTheRoll'),
        label: t('parentNotice.noLongerEnrolled', { count: orphans.length }),
        members: orphans.map(sid => ({ sid, name: sid, currentClass: '', classNumber: '' })),
      });
    }
    return rows;
  }, [students, chosen, levelFor, levelLabel]);

  const pending = useMemo(() => {
    if (scope === 'school') return students;
    if (sel.size === 0) return [];
    if (scope === 'level') return students.filter(st => sel.has(levelFor(st.currentClass || '—')));
    if (scope === 'class') return students.filter(st => sel.has(st.currentClass || '—'));
    return students.filter(st => sel.has(st.sid));
  }, [scope, sel, students, levelFor]);

  const addPending = () => {
    if (pending.length === 0) return;
    onChange([...new Set([...value, ...pending.map(st => st.sid)])]);
    setSel(new Set());
    setSearch('');
  };

  const removeRow = (row) => {
    const gone = new Set(row.members.map(m => m.sid));
    onChange(value.filter(sid => !gone.has(sid)));
  };

  const tickBox = { width: '15px', height: '15px', cursor: 'pointer', accentColor: 'var(--color-primary)', flexShrink: 0 };
  const tickRow = (on) => ({
    display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.35rem 0.6rem',
    cursor: 'pointer', fontSize: '0.83rem',
    backgroundColor: on ? 'var(--color-primary-light)' : 'transparent',
  });
  const boxed = { border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', maxHeight: '190px', overflowY: 'auto' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.55rem' }}>
      <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap', alignItems: 'center' }}>
        {SCOPES.map(id => (
          <button key={id} type="button" disabled={disabled}
                  onClick={() => switchScope(id)}
                  style={chipStyle(scope === id)}>
            {t(`parentNotice.scopes.${id}`)}
          </button>
        ))}
        {/* marginLeft rather than space-between: if the scope chips ever wrap, Add drops to its own
            line still right-aligned instead of being pulled away from them. */}
        <button type="button" className="btn-primary" onClick={addPending}
                disabled={disabled || pending.length === 0}
                style={{ width: 'auto', padding: '0.4rem 1rem', marginLeft: 'auto', whiteSpace: 'nowrap' }}>
          <Plus size={15} /> Add{pending.length > 0 ? ` ${pending.length} student${pending.length === 1 ? '' : 's'}` : ''}
        </button>
      </div>

      {scope === 'school' && (
        <div style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)' }}>
          Every student currently enrolled ({students.length}).
        </div>
      )}

      {(scope === 'level' || scope === 'class') && (
        <div style={{ ...boxed, display: 'flex', flexWrap: 'wrap', gap: '0.2rem', padding: '0.35rem' }}>
          {(scope === 'level'
            ? levelOptions.map(([code, label]) => ({ key: code, label, size: students.filter(st => levelFor(st.currentClass || '—') === code).length }))
            : classOptions.map(c => ({ key: c, label: c, size: students.filter(st => st.currentClass === c).length }))
          ).map(opt => (
            <label key={opt.key} style={{ ...tickRow(sel.has(opt.key)), borderRadius: 'var(--radius-md)', flex: '0 0 auto' }}>
              <input type="checkbox" checked={sel.has(opt.key)} onChange={() => toggleSel(opt.key)} disabled={disabled} style={tickBox} />
              <span style={{ fontWeight: '600' }}>{opt.label}</span>
              <span style={{ color: 'var(--color-text-muted)' }}>{opt.size}</span>
            </label>
          ))}
        </div>
      )}

      {scope === 'student' && (
        <>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <select className="input-field" value={classFilter} onChange={e => setClassFilter(e.target.value)} disabled={disabled}
                    style={{ margin: 0, flex: '0 1 170px', minWidth: 0 }}>
              <option value="">{t('parentNotice.allClasses')}</option>
              {classOptions.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <div style={{ position: 'relative', flex: '1 1 180px', minWidth: 0 }}>
              <Search size={15} style={{ position: 'absolute', left: '0.6rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-muted)' }} />
              <input className="input-field" value={search} onChange={e => setSearch(e.target.value)} disabled={disabled}
                     placeholder={t('parentNotice.searchStudents')} style={{ margin: 0, paddingLeft: '2rem' }} />
            </div>
          </div>

          <div style={boxed}>
            {matches.length === 0 ? (
              <div style={{ padding: '0.75rem', fontSize: '0.83rem', color: 'var(--color-text-muted)' }}>{t('parentNotice.noMatches')}</div>
            ) : (
              <>
                {/* Tick a class in the filter, then take the lot. Without this, "3B except two of
                    them" means thirty clicks. */}
                <label style={{ ...tickRow(false), borderBottom: '1px solid var(--color-border)', fontWeight: '600', position: 'sticky', top: 0, backgroundColor: 'var(--color-bg-app)' }}>
                  <input type="checkbox" disabled={disabled} style={tickBox}
                         checked={matches.every(st => sel.has(st.sid))}
                         onChange={e => setSel(prev => {
                           const next = new Set(prev);
                           matches.forEach(st => (e.target.checked ? next.add(st.sid) : next.delete(st.sid)));
                           return next;
                         })} />
                  <span>{t('parentNotice.selectAllShown', { count: matches.length })}</span>
                </label>
                {matches.map(st => (
                  <label key={st.sid} style={tickRow(sel.has(st.sid))}>
                    <input type="checkbox" checked={sel.has(st.sid)} onChange={() => toggleSel(st.sid)} disabled={disabled} style={tickBox} />
                    <span>{st.label}</span>
                  </label>
                ))}
              </>
            )}
          </div>
        </>
      )}

      <div style={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
        {summary.length === 0 ? (
          <div style={{ padding: '1rem', textAlign: 'center', fontSize: '0.83rem', color: 'var(--color-text-muted)' }}>
            {t('parentNotice.nobodyAdded')}
          </div>
        ) : summary.map(row => {
          const open = openRow === row.key;
          const expandable = row.members.length > 1;
          return (
            <div key={row.key} style={{ borderTop: row.key === summary[0].key ? 'none' : '1px solid var(--color-border)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.45rem 0.6rem' }}>
                <button type="button" disabled={!expandable}
                        onClick={() => setOpenRow(open ? null : row.key)}
                        style={{ background: 'none', border: 'none', padding: 0, display: 'flex', alignItems: 'center', gap: '0.4rem', flex: 1, minWidth: 0, textAlign: 'left', cursor: expandable ? 'pointer' : 'default' }}>
                  {expandable
                    ? (open ? <ChevronDown size={15} style={{ flexShrink: 0 }} /> : <ChevronRight size={15} style={{ flexShrink: 0 }} />)
                    : <span style={{ width: 15, flexShrink: 0 }} />}
                  <span style={{ fontSize: '0.7rem', fontWeight: '700', color: 'var(--color-text-muted)', flexShrink: 0 }}>{row.kind}</span>
                  <span style={{ fontSize: '0.85rem', fontWeight: '600', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.label}</span>
                  {expandable && (
                    <span style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)', flexShrink: 0 }}>{row.members.length}</span>
                  )}
                </button>
                <button type="button" onClick={() => removeRow(row)} disabled={disabled} aria-label={t('parentNotice.removeRow', { label: row.label })}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-danger)', display: 'flex', padding: '0.2rem', flexShrink: 0 }}>
                  <X size={15} />
                </button>
              </div>

              {open && (
                <div style={{ maxHeight: '180px', overflowY: 'auto', backgroundColor: 'var(--color-bg-app)', borderTop: '1px solid var(--color-border)' }}>
                  {[...row.members]
                    .sort((a, b) => (a.currentClass || '').localeCompare(b.currentClass || '', undefined, { numeric: true }) || Number(a.classNumber || 0) - Number(b.classNumber || 0))
                    .map(m => (
                      <div key={m.sid} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem', padding: '0.3rem 0.6rem 0.3rem 2rem', fontSize: '0.8rem' }}>
                        <span>{m.currentClass || '—'} ({m.classNumber || '—'}) {m.name}</span>
                        {/* Taking one person out of a class you added should not mean removing the
                            class and rebuilding it. */}
                        <button type="button" onClick={() => onChange(value.filter(sid => sid !== m.sid))} disabled={disabled}
                                aria-label={t('parentNotice.removeRow', { label: m.name })}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', display: 'flex', padding: '0.1rem', flexShrink: 0 }}>
                          <X size={13} />
                        </button>
                      </div>
                    ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const ParentNotice = () => {
  const { t } = useT();
  // The stored value is the key; the English label stays the fallback.
  const statusWord = (v) => t(`parentNotice.status.${v}`, {}, STATUS_LABEL[v]);
  const { activeSchoolId, isMobile, userProfile, user, closeSidebar } = useOutletContext();

  const ownEmail = (user?.email || '').toLowerCase();
  const ownRole = String(userProfile?.role || '').toLowerCase();
  const isAdmin = isAdminRole(ownRole);
  const isStudent = ownRole === 'student';
  const isParent = ownRole === 'parent';
  const ownSid = userProfile?.studentId || '';
  const ownName = userProfile?.name || userProfile?.initial || ownEmail;

  // userProfile is null until it resolves, so on a hard refresh EVERY flag above is false for the
  // first render -- nobody is a student yet, which means "if (isStudent) return" reads as "carry on"
  // and the staff loaders fire for a student. Nothing role-dependent may run before this is true.
  const roleKnown = !!userProfile && !!ownRole;

  const [activeTab, setActiveTab] = useState('outstanding');

  // Student-facing: the notices addressed to me, and which of them I have already signed.

  const [notices, setNotices] = useState([]);
  const [signatures, setSignatures] = useState([]);
  const [students, setStudents] = useState([]);
  const [staff, setStaff] = useState([]);

  // Who may issue a notice. Like Salary Audit, the default is nobody -- an admin names each person,
  // and admins always retain access so a bad list can't lock the school out.
  const [authorizedStaff, setAuthorizedStaff] = useState([]);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [staffSearch, setStaffSearch] = useState('');

  const [payments, setPayments] = useState([]);

  // Finding one letter among a year's worth of them. Both lists share the search box and the year,
  // because somebody looking for "swimming gala" does not know or care whether the school sent it
  // as a notice or a fee.
  const [search, setSearch] = useState('');
  const [yearFilter, setYearFilter] = useState('');
  const [academicYears, setAcademicYears] = useState([]);
  const [yearPicked, setYearPicked] = useState(false);
  const [paymentRecords, setPaymentRecords] = useState([]);
  const [expandedPayments, setExpandedPayments] = useState(new Set());
  const [showPay, setShowPay] = useState(false);
  const [payForm, setPayForm] = useState({ title: '', description: '', amount: '', deadline: '' });
  const [payTargets, setPayTargets] = useState([]);
  const [creatingPayment, setCreatingPayment] = useState(false);

  const [showIssue, setShowIssue] = useState(false);
  // The notice currently open in the form, or null when drafting a new one. Editing exists because
  // approval would otherwise be a dead end: a notice sent back has to be able to come forward again,
  // and the office fixing a typo should not have to find whoever drafted it.
  const [editing, setEditing] = useState(null);
  // No eventDates here any more: a notice raised from this screen is not about particular days, and
  // asking every user to consider a date list they almost never need cost more than it earned.
  //
  // The FIELD stays on the notice document. OLE still fills it from an activity's session dates when
  // it raises a notice, and MyNotice still renders whatever is there -- removing it from this form is
  // a decision about who types it, not about what a notice can carry.
  const [form, setForm] = useState({ title: '', content: '', issuerId: '' });
  const isNoticeModuleAdmin = useModuleAdmin(activeSchoolId, 'parent_notice');
  const [templates, setTemplates] = useState([]);
  const [schoolName, setSchoolName] = useState('');
  // Who signs off, and at which of the two stages. Both stages always apply -- a school that has
  // named nobody for a stage leaves it to the system administrators, who can always approve, so a
  // notice is never stuck with no possible approver. There is no way to switch approval off.
  const [approvalSettings, setApprovalSettings] = useState({});
  const myStages = stageFor(approvalSettings, { email: ownEmail });

  // Which form level a class sits in, and what that level is called. Both come from System
  // Settings, because that is where the school defines them.
  const [classMeta, setClassMeta] = useState({ classes: [], levels: [] });

  // A class belongs to a level because the school said so, not because of how its code is spelled.
  // The leading-characters fallback exists only so the strips still group sensibly for a school
  // that has not filled the level in -- 6A and 6B still land together -- but configuration wins.
  const levelFor = useCallback((cls) => {
    const hit = (classMeta.classes || []).find(c => c.code === cls || c.nameEn === cls || c.name === cls);
    if (hit?.level) return hit.level;
    const lead = String(cls || '').match(/^[A-Za-z]*[0-9]+/);
    return lead ? lead[0] : (cls || 'Other');
  }, [classMeta]);

  const levelLabel = useCallback((code) => {
    const hit = (classMeta.levels || []).find(l => l.code === code);
    return hit ? (hit.nameEn || hit.name || code) : code;
  }, [classMeta]);

  // The templates a notice can start from, and the one value they need that this module does not
  // otherwise hold. Loaded together because both are only used by the issue form.
  useEffect(() => {
    if (!activeSchoolId) return undefined;
    getDoc(doc(db, 'schools', activeSchoolId, 'metadata', 'system_settings'))
      .then(snap => {
        const data = snap.exists() ? snap.data() : {};
        setSchoolName(data.schoolName || '');
        // Same document, no extra read. The roster strips need to know which classes belong to
        // which form level, and the school has already said so here.
        setClassMeta({
          classes: data.dropdowns?.classes || [],
          levels: data.dropdowns?.classLevels || [],
        });
      })
      .catch(() => {});
    return onSnapshot(
      collection(db, 'schools', activeSchoolId, 'notice_template'),
      snap => setTemplates(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
      e => console.error('Error loading notice templates', e),
    );
  }, [activeSchoolId]);
  const [file, setFile] = useState(null);
  const [targets, setTargets] = useState([]);
  const [issuing, setIssuing] = useState(false);

  const canIssue = isAdmin || authorizedStaff.includes(ownEmail);

  // --- Reference data -------------------------------------------------------

  useEffect(() => {
    if (!activeSchoolId || !roleKnown) return;
    if (isStudent || isParent) { setSettingsLoaded(true); return; }

    const load = async () => {
      try {
        const snap = await getDocs(collection(db, 'schools', activeSchoolId, 'student_profiles'));
        const list = [];
        snap.forEach(d => {
          const data = d.data();
          if (!data.sid) return;
          const name = `${data.lastName || ''} ${data.firstName || ''}`.trim() || data.sid;
          list.push({
            sid: data.sid,
            email: (data.email || d.id || '').toLowerCase(),
            guardianEmail: (data.guardianEmail || '').toLowerCase(),
            guardianEmail2: (data.guardianEmail2 || '').toLowerCase(),
            name,
            chineseName: data.chineseName || '',
            currentClass: data.currentClass || '',
            classNumber: data.classNumber || '',
            label: `${data.currentClass || '—'} (${data.classNumber || '—'}) - ${name} (${data.sid})`,
          });
        });
        list.sort((a, b) => {
          const cls = String(a.currentClass).localeCompare(String(b.currentClass), undefined, { numeric: true });
          if (cls !== 0) return cls;
          return (Number(a.classNumber) || 0) - (Number(b.classNumber) || 0);
        });
        setStudents(list);
      } catch (e) { console.error('Error loading students', e); }

      try {
        const snap = await getDocs(collection(db, 'schools', activeSchoolId, 'staff_profiles'));
        const list = [];
        snap.forEach(d => {
          const data = d.data();
          const email = (data.email || d.id || '').toLowerCase();
          if (!email.includes('@')) return;
          const name = `${data.lastName || ''} ${data.firstName || ''}`.trim() || data.initial || email;
          list.push({ email, initial: data.initial || email, name, label: `${data.initial || email} — ${name}` });
        });
        list.sort((a, b) => a.initial.localeCompare(b.initial));
        setStaff(list);
      } catch (e) { console.error('Error loading staff', e); }

      // Who may issue notices now lives in metadata/module_admins with every other module's
      // delegation, and that is the document the security rules read. Reading it here rather than
      // parent_notice_settings.authorizedStaff keeps the button and the rule agreeing.
      try {
        const s = await getDoc(doc(db, 'schools', activeSchoolId, 'metadata', 'module_admins'));
        const data = s.exists() ? s.data() : {};
        setAuthorizedStaff((data.parent_notice || []).map(e => String(e).toLowerCase()));
      } catch (e) { console.error('Error loading module admins', e); }

      try {
        const s = await getDoc(doc(db, 'schools', activeSchoolId, 'metadata', 'parent_notice_settings'));
        setApprovalSettings(s.exists() ? s.data() : {});
      } catch (e) { console.error('Error loading approval settings', e); }
      setSettingsLoaded(true);
    };

    load();
  }, [activeSchoolId, roleKnown, isStudent, isParent]);

  const studentBySid = useMemo(() => Object.fromEntries(students.map(s => [s.sid, s])), [students]);

  // Denormalised recipient list, written onto every notice and payment item at creation time.
  // The security rule has no way to look up who a target SID belongs to -- it can only test fields
  // on the document in front of it -- so the addressees' own emails have to live on the document.
  // That is what lets the rule below be "you may read this only if you are named on it", instead of
  // the far weaker "you may read this if you belong to the school".
  // Writes one row per (thing, student). The parent document keeps the roster for staff; these rows
  // are what a family reads, and each names exactly one student -- which is the whole point, since a
  // security rule cannot return part of a document.
  //
  // Batched in chunks of 400: Firestore caps a batch at 500 writes, and a whole-school notice is
  // well past that.
  // Thin wrappers over src/noticeFanOut.js.
  //
  // OLE Management is about to issue notices too, and this is the piece that has already had both a
  // roster leak and a timeout -- having two copies of it is not a risk worth taking. Four separate
  // "authorised staff" lists elsewhere in this platform all started as one module quietly growing
  // its own version of something another module already did.
  const fanOut = useCallback(
    (collectionName, keyPrefix, sids, rowFor, merge = false) => fanOutRows({
      db, schoolId: activeSchoolId, collectionName, keyPrefix, sids, studentBySid, rowFor, merge,
    }),
    [activeSchoolId, studentBySid],
  );

  const audienceFor = useCallback(
    (sids) => audienceForSids(studentBySid, sids),
    [studentBySid],
  );

  // A document needs repair if it has no recipient list at all, or if the list no longer matches the
  // profiles -- adding a guardian to a student after a notice went out leaves that guardian unable
  // to see it, which looks identical to the notice being lost.
  const audienceStale = useCallback((d) => {
    if (!Array.isArray(d.audienceEmails)) return true;
    const want = audienceFor(d.targetSids || []);
    if (want.length !== d.audienceEmails.length) return true;
    const have = new Set(d.audienceEmails);
    return want.some(e => !have.has(e));
  }, [audienceFor]);

  const staleAudienceDocs = useMemo(() => [
    ...notices.filter(audienceStale).map(d => ({ col: 'parent_notice', d })),
    ...payments.filter(audienceStale).map(d => ({ col: 'payment_item', d })),
  ], [notices, payments, audienceStale]);
  const classOptions = useMemo(
    () => [...new Set(students.map(s => s.currentClass).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b, undefined, { numeric: true })),
    [students]
  );

  // --- Notices --------------------------------------------------------------

  // The years the school actually defined, with their real start and end dates. The year of a
  // notice is worked out from these rather than stored on it -- see src/noticeFilter.js.
  useEffect(() => {
    if (!activeSchoolId) { setAcademicYears([]); return; }
    getDocs(collection(db, 'schools', activeSchoolId, 'academic_years'))
      .then(snap => {
        const list = [];
        snap.forEach(d => list.push({ uid: d.id, ...d.data() }));
        setAcademicYears(list);
      })
      .catch(e => console.error('Error loading academic years', e));
  }, [activeSchoolId]);

  useEffect(() => {
    if (!activeSchoolId || !roleKnown || isStudent || isParent) { setNotices([]); return; }
    const unsub = onSnapshot(collection(db, 'schools', activeSchoolId, 'parent_notice'), (snap) => {
      const list = [];
      snap.forEach(d => list.push({ uid: d.id, ...d.data() }));
      list.sort((a, b) => String(b.issue_datetime || '').localeCompare(String(a.issue_datetime || '')));
      setNotices(list);
    }, (e) => console.error('Error loading notices', e));
    return () => unsub();
  }, [activeSchoolId, roleKnown, isStudent, isParent]);

  // Only signed replies are stored. Outstanding is derived as "targets minus signed", which avoids
  // writing a row per student at issue time -- a notice to the whole school would otherwise cost
  // hundreds of writes before anyone had responded.
  useEffect(() => {
    if (!activeSchoolId || !roleKnown || isStudent || isParent) { setSignatures([]); return; }
    const unsub = onSnapshot(collection(db, 'schools', activeSchoolId, 'parent_notice_signature'), (snap) => {
      const list = [];
      snap.forEach(d => list.push({ uid: d.id, ...d.data() }));
      setSignatures(list);
    }, (e) => console.error('Error loading signatures', e));
    return () => unsub();
  }, [activeSchoolId, roleKnown, isStudent, isParent]);

  // Outstanding is now a query, not a subtraction. Each recipient row carries `signed`, kept in step
  // by the syncSignedFlag trigger -- so this asks Firestore for the unsigned rows instead of reading
  // every target and every signature and computing the difference in the browser.
  //
  // The rows are still read whole here because the same subscription drives the counts on every
  // notice; the point of the flag is that "unsigned" is a stored fact rather than a derived one.
  const [recipients, setRecipients] = useState([]);
  useEffect(() => {
    if (!activeSchoolId || !roleKnown || isStudent || isParent) { setRecipients([]); return; }
    const unsub = onSnapshot(
      collection(db, 'schools', activeSchoolId, 'parent_notice_recipient'),
      (snap) => {
        const list = [];
        snap.forEach(d => list.push({ uid: d.id, ...d.data() }));
        setRecipients(list);
      },
      (e) => console.error('Error loading notice recipients', e),
    );
    return () => unsub();
  }, [activeSchoolId, roleKnown, isStudent, isParent]);

  const signedByNotice = useMemo(() => {
    const map = {};
    recipients.forEach(r => {
      if (r.signed) (map[r.noticeId] = map[r.noticeId] || new Set()).add(r.sid);
    });
    return map;
  }, [recipients]);

  const outstandingFor = useCallback((notice) => recipients
    .filter(r => r.noticeId === notice.uid && !r.signed)
    .map(r => studentBySid[r.sid] || { sid: r.sid, name: r.sid, currentClass: '', classNumber: '', label: r.sid }),
    [recipients, studentBySid]);

  // Everyone the notice reached, carrying whether they have signed. The table needs the signed rows
  // too: staff work down a whole class asking who has handed the slip in, and a row that disappears
  // the instant it is ticked leaves no way to undo a mis-click. The counts above still come from
  // outstandingFor, so "N outstanding" keeps meaning outstanding.
  const rosterFor = useCallback((notice) => recipients
    .filter(r => r.noticeId === notice.uid)
    .map(r => ({
      ...(studentBySid[r.sid] || { sid: r.sid, name: r.sid, currentClass: '', classNumber: '', label: r.sid }),
      done: !!r.signed,
    })),
    [recipients, studentBySid]);

  // Collapsed by default: a notice to a whole form is a very long table, and staff normally care
  // about one notice at a time.
  const [expandedNotices, setExpandedNotices] = useState(new Set());
  const toggleExpanded = (id) => setExpandedNotices(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  // --- Payments -------------------------------------------------------------

  useEffect(() => {
    if (!activeSchoolId || !roleKnown) return;
    // Staff see every fee; a student or guardian sees only the ones they are named on. Previously
    // this was an unscoped read for everybody, so any student could enumerate every fee in the
    // school along with its amount.
    const src = (isStudent || isParent)
      ? query(collection(db, 'schools', activeSchoolId, 'payment_item'),
              where('audienceEmails', 'array-contains', ownEmail))
      : collection(db, 'schools', activeSchoolId, 'payment_item');
    const unsub = onSnapshot(src, (snap) => {
      const list = [];
      snap.forEach(d => list.push({ uid: d.id, ...d.data() }));
      list.sort((a, b) => String(b.created_datetime || '').localeCompare(String(a.created_datetime || '')));
      setPayments(list);
    }, (e) => console.error('Error loading payments', e));
    return () => unsub();
  }, [activeSchoolId, roleKnown, isStudent, isParent, ownEmail]);

  useEffect(() => {
    if (!activeSchoolId || !roleKnown) return;
    // Staff read every settlement; a guardian may only read their own, so the query is scoped to
    // whichever rule applies to this account.
    const src = (isStudent || isParent)
      ? query(collection(db, 'schools', activeSchoolId, 'payment_record'),
              where(isParent ? 'guardianEmail' : 'studentEmail', '==', ownEmail))
      : collection(db, 'schools', activeSchoolId, 'payment_record');
    const unsub = onSnapshot(src, (snap) => {
      const list = [];
      snap.forEach(d => list.push({ uid: d.id, ...d.data() }));
      setPaymentRecords(list);
    }, (e) => console.error('Error loading payment records', e));
    return () => unsub();
  }, [activeSchoolId, roleKnown, isStudent, isParent, ownEmail]);

  const paidByPayment = useMemo(() => {
    const map = {};
    paymentRecords.forEach(r => {
      if (r.status !== 'paid') return;
      (map[r.payId] = map[r.payId] || new Set()).add(r.sid);
    });
    return map;
  }, [paymentRecords]);

  const unpaidFor = useCallback((payment) => {
    const paid = paidByPayment[payment.uid] || new Set();
    return (payment.targetSids || [])
      .filter(sid => !paid.has(sid))
      .map(sid => studentBySid[sid] || { sid, name: sid, currentClass: '', classNumber: '' });
  }, [paidByPayment, studentBySid]);

  // Same reasoning as rosterFor: the settled rows stay on screen so a settlement recorded against
  // the wrong student can be found and removed.
  const paymentRosterFor = useCallback((payment) => {
    const paid = paidByPayment[payment.uid] || new Set();
    return (payment.targetSids || []).map(sid => ({
      ...(studentBySid[sid] || { sid, name: sid, currentClass: '', classNumber: '' }),
      done: paid.has(sid),
    }));
  }, [paidByPayment, studentBySid]);

  const togglePaymentExpanded = (id) => setExpandedPayments(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const handleCreatePayment = async (e) => {
    e.preventDefault();
    const cents = toCents(payForm.amount);
    if (!payForm.title.trim() || cents <= 0 || payTargets.length === 0) return;

    setCreatingPayment(true);
    try {
      const pid = paymentId();
      await setDoc(doc(db, 'schools', activeSchoolId, 'payment_item', pid), {
        payId: pid,
        title: payForm.title.trim(),
        description: payForm.description.trim(),
        amountCents: cents,
        currency: 'HKD',
        deadline: payForm.deadline || '',
        targetSids: payTargets,
        targetCount: payTargets.length,
        audienceEmails: audienceFor(payTargets),
        createdByEmail: ownEmail,
        createdByName: ownName,
        created_datetime: new Date().toISOString(),
        status: 'open',
      });
      await fanOut('payment_assignment', pid, payTargets, () => ({
        payId: pid,
        title: payForm.title.trim(),
        description: payForm.description.trim(),
        amountCents: cents,
        currency: 'HKD',
        deadline: payForm.deadline || '',
        created_datetime: new Date().toISOString(),
      }));

      setShowPay(false);
      setPayForm({ title: '', description: '', amount: '', deadline: '' });
      setPayTargets([]);
      setExpandedPayments(new Set([pid]));
    } catch (err) {
      console.error('Error creating payment', err);
      alert(t('parentNotice.createPaymentFailed', { message: err.message }));
    }
    setCreatingPayment(false);
  };

  // Staff-recorded settlement, for cash or a bank transfer taken outside K-Pay. An ONLINE payment
  // must never be confirmed from a browser -- see the note on the Firestore rule: only a server-side
  // callback from the payment provider should ever record method 'kpay'.
  const markPaid = async (payment, student) => {
    const existing = paymentRecords.find(r => r.payId === payment.uid && r.sid === student.sid);
    try {
      if (existing && existing.status === 'paid') {
        if (!window.confirm(t('parentNotice.removeSettlementConfirm', { name: student.name || student.sid }))) return;
        await deleteDoc(doc(db, 'schools', activeSchoolId, 'payment_record', paymentDocId(payment.uid, student.sid)));
        return;
      }

      // A reason is required rather than optional: this path bypasses K-Pay, so without one there is
      // nothing in the record explaining why the school asserted a payment it cannot evidence.
      const reason = window.prompt(
        t('parentNotice.settlementConfirm', { name: student.name || student.sid })
      );
      if (reason === null) return;
      if (!reason.trim()) { alert(t('parentNotice.reasonRequired')); return; }
      const child = studentBySid[student.sid] || student;
      await setDoc(doc(db, 'schools', activeSchoolId, 'payment_record', paymentDocId(payment.uid, student.sid)), {
        payId: payment.uid,
        sid: student.sid,
        studentEmail: child.email || '',
        guardianEmail: child.guardianEmail || '',
        studentName: child.name || student.sid,
        currentClass: child.currentClass || '',
        classNumber: child.classNumber || '',
        amountCents: payment.amountCents || 0,
        status: 'paid',
        settledVia: SETTLED_VIA.SCHOOL_OVERRIDE,
        overrideReason: reason.trim(),
        reference: '',
        paidAt: new Date().toISOString(),
        recordedByEmail: ownEmail,
        recordedByName: ownName,
      });
    } catch (err) {
      alert(t('parentNotice.updateFailed', { message: err.message }));
    }
  };

  const handleDeletePayment = async (payment) => {
    if (!window.confirm(t('parentNotice.deletePaymentConfirm', { title: payment.title }))) return;
    try {
      const recs = await getDocs(query(
        collection(db, 'schools', activeSchoolId, 'payment_record'),
        where('payId', '==', payment.uid)
      ));
      for (let i = 0; i < recs.docs.length; i += 400) {
        const batch = writeBatch(db);
        recs.docs.slice(i, i + 400).forEach(d => batch.delete(d.ref));
        await batch.commit();
      }
      await deleteDoc(doc(db, 'schools', activeSchoolId, 'payment_item', payment.uid));
    } catch (err) {
      alert(t('parentNotice.deleteFailed', { message: err.message }));
    }
  };

  // --- Issue ----------------------------------------------------------------

  // Moves a notice along, or sends it back.
  //
  // Approving the final stage is what puts it in front of families: the recipient rows are written
  // here, from the notice as it stands at the moment of approval. Doing it any earlier would mean an
  // unapproved letter was already readable.
  const decideNotice = async (notice, action) => {
    const stage = notice.status === STATUS.PENDING_FINAL ? 'final' : 'first';
    let note = '';
    if (action === 'reject') {
      note = window.prompt(t('parentNotice.whyGoingBack')) || '';
      if (!note.trim()) { alert(t('parentNotice.reasonRequiredSendBack')); return; }
    }

    try {
      const entry = logEntry({ action, byEmail: ownEmail, byName: ownName, stage, note });
      let next;
      if (action === 'reject') next = STATUS.REJECTED;
      else next = stage === 'first' ? STATUS.PENDING_FINAL : STATUS.APPROVED;

      if (next === STATUS.APPROVED) {
        const sids = notice.targetSids || [];
        await fanOut('parent_notice_recipient', notice.noticeId, sids, () => ({
          noticeId: notice.noticeId,
          title: notice.title,
          content: notice.content,
          issuerId: notice.issuerId || '',
          issuerName: notice.issuerName || '',
          issue_datetime: notice.issue_datetime,
          eventDates: notice.eventDates || [],
          attachmentUrl: notice.attachmentUrl || '',
          attachmentName: notice.attachmentName || '',
          signed: false,
        }));
      }

      await setDoc(doc(db, 'schools', activeSchoolId, 'parent_notice', notice.uid), {
        status: next,
        approvalLog: [...(notice.approvalLog || []), entry],
      }, { merge: true });
    } catch (e) {
      console.error('Approval failed', e);
      alert(t('parentNotice.recordFailed', { message: e.message }));
    }
  };

  const closeForm = () => {
    setShowIssue(false);
    setEditing(null);
    setForm({ title: '', content: '', issuerId: '' });
    setFile(null); setTargets([]);
  };

  // Who may open a notice for editing: whoever drafted it while it is still theirs to fix, and the
  // module admins at any point. A drafter cannot touch it once it has left them -- otherwise the
  // text could change under an approver between reading and signing.
  const canEdit = (n) => isAdmin || isNoticeModuleAdmin
    || (n.issuedByEmail === ownEmail
        && (n.status === STATUS.PENDING_FIRST || n.status === STATUS.REJECTED));

  const openEdit = (n) => {
    setEditing(n);
    setForm({
      title: n.title || '',
      content: n.content || '',
      issuerId: n.issuerEmail || '',
    });
    setTargets(n.targetSids || []);
    setFile(null);
    setShowIssue(true);
  };

  const handleIssue = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.content.trim() || targets.length === 0) return;

    setIssuing(true);
    try {
      const nid = editing ? editing.noticeId : noticeId();
      const now = new Date().toISOString();
      // An edit keeps whatever was already attached unless a new file is chosen: re-uploading the
      // same PDF just to change a date in the text would be absurd.
      let attachmentUrl = editing?.attachmentUrl || '';
      let attachmentName = editing?.attachmentName || '';
      let attachmentPath = editing?.attachmentPath || '';

      if (file) {
        attachmentPath = `upload/schools/${activeSchoolId}/parent_notice/${nid}/${file.name}`;
        const snapshot = await uploadBytesResumable(ref(storage, attachmentPath), file);
        attachmentUrl = await getDownloadURL(snapshot.ref);
        attachmentName = file.name;
      }

      const issuer = staff.find(s => s.email === form.issuerId);

      // An edited notice goes back to the start of approval unless it is already out. Sending a
      // changed letter forward on a signature given for the earlier text would make the approval
      // record a lie -- so only an approved notice keeps its status, and the edit is logged instead.
      const wasApproved = editing && editing.status === STATUS.APPROVED;
      const status = editing
        ? (wasApproved ? STATUS.APPROVED : firstPendingStatus())
        : firstPendingStatus();
      const entry = logEntry({
        action: editing ? 'edit' : 'submit',
        byEmail: ownEmail,
        byName: ownName,
        note: wasApproved ? t('parentNotice.editedAfterApproval') : '',
      });

      await setDoc(doc(db, 'schools', activeSchoolId, 'parent_notice', nid), {
        noticeId: nid,
        title: form.title.trim(),
        content: form.content.trim(),
        // The days this notice covers. Empty for a notice not about particular days.
          status,
        approvalLog: [...(editing?.approvalLog || []), entry],
        attachmentUrl, attachmentName, attachmentPath,
        // The issuer is who the notice is FROM (often the Principal); issuedBy records who actually
        // pressed the button, so the two are never conflated in an audit.
        issuerEmail: issuer?.email || '',
        issuerId: issuer?.initial || '',
        issuerName: issuer?.name || '',
        issuedByEmail: editing?.issuedByEmail || ownEmail,
        issuedByName: editing?.issuedByName || ownName,
        issue_datetime: editing?.issue_datetime || now,
        targetSids: targets,
        targetCount: targets.length,
        audienceEmails: audienceFor(targets),
      }, { merge: true });

      // A new or still-unapproved notice is not fanned out here. The recipient rows ARE the notice
      // as far as a family is concerned, so "not approved" and "not visible" are the same state --
      // there is no window in which an unapproved letter is sitting where a parent could read it.
      // The rows get written by decideNotice() at final approval.
      //
      // An already-approved notice is the one case with rows to keep in step, and they are merged so
      // that families who have already signed stay signed.
      if (wasApproved) {
        await fanOut('parent_notice_recipient', nid, targets, () => ({
          noticeId: nid,
          title: form.title.trim(),
          content: form.content.trim(),
          issuerId: issuer?.initial || '',
          issuerName: issuer?.name || '',
          issue_datetime: editing.issue_datetime || now,
              attachmentUrl, attachmentName,
        }), true);
      }

      closeForm();
      setExpandedNotices(new Set([nid]));
      alert(editing
        ? (wasApproved
          ? t('parentNotice.savedEditedLive')
          : t('parentNotice.savedSentBack'))
        : t('parentNotice.submittedForApproval'));
    } catch (err) {
      console.error('Error issuing notice', err);
      alert(t('parentNotice.saveNoticeFailed', { message: err.message }));
    }
    setIssuing(false);
  };

  // Recomputes the recipient list for anything issued before the field existed, or whose targets'
  // profiles have changed since. Repairs in place, is idempotent, and touches only what is stale.
  const [backfilling, setBackfilling] = useState(false);
  const handleBackfillAudience = async () => {
    if (staleAudienceDocs.length === 0) { alert(t('parentNotice.nothingToRepair')); return; }
    if (!window.confirm(t('parentNotice.repairConfirm', { count: staleAudienceDocs.length }))) return;

    setBackfilling(true);
    let done = 0, failed = 0;
    for (const { col, d } of staleAudienceDocs) {
      try {
        await setDoc(doc(db, 'schools', activeSchoolId, col, d.uid),
          { audienceEmails: audienceFor(d.targetSids || []) }, { merge: true });
        done += 1;
      } catch (e) {
        console.error('Backfill failed for', col, d.uid, e);
        failed += 1;
      }
    }
    setBackfilling(false);
    alert(failed
      ? t('parentNotice.repairedWithFailures', { count: done, failed })
      : t('parentNotice.repaired', { count: done }));
  };

  const toggleSigned = async (notice, student) => {
    if (!notice) return;
    const id = signatureDocId(notice.uid, student.sid);
    const existing = signatures.find(s => s.noticeId === notice.uid && s.sid === student.sid);
    try {
      if (existing) {
        await deleteDoc(doc(db, 'schools', activeSchoolId, 'parent_notice_signature', id));
      } else {
        await setDoc(doc(db, 'schools', activeSchoolId, 'parent_notice_signature', id), {
          noticeId: notice.uid,
          sid: student.sid,
          studentEmail: student.email || '',
          studentName: student.name,
          currentClass: student.currentClass,
          classNumber: student.classNumber,
          signedAt: new Date().toISOString(),
          // Recorded by staff on a parent's behalf -- there is no parent-facing screen yet.
          recordedByEmail: ownEmail,
          recordedByName: ownName,
        });
      }
    } catch (err) {
      alert(t('parentNotice.updateFailed', { message: err.message }));
    }
  };

  const markAllSigned = async (notice) => {
    const rows = outstandingFor(notice);
    if (!notice || rows.length === 0) return;
    if (!window.confirm(t('parentNotice.markAllSignedConfirm', { count: rows.length, title: notice.title }))) return;
    try {
      const now = new Date().toISOString();
      for (let i = 0; i < rows.length; i += 400) {
        const batch = writeBatch(db);
        rows.slice(i, i + 400).forEach(s => {
          batch.set(doc(db, 'schools', activeSchoolId, 'parent_notice_signature', signatureDocId(notice.uid, s.sid)), {
            noticeId: notice.uid,
            sid: s.sid, studentEmail: s.email || '', guardianEmail: s.guardianEmail || '', studentName: s.name,
            currentClass: s.currentClass, classNumber: s.classNumber,
            signedAt: now, recordedByEmail: ownEmail, recordedByName: ownName,
          });
        });
        await batch.commit();
      }
    } catch (err) {
      alert(t('parentNotice.failed', { message: err.message }));
    }
  };

  const handleDeleteNotice = async (notice) => {
    if (!window.confirm(t('parentNotice.deleteNoticeConfirm', { title: notice.title }))) return;
    try {
      const sigs = await getDocs(query(
        collection(db, 'schools', activeSchoolId, 'parent_notice_signature'),
        where('noticeId', '==', notice.uid)
      ));
      const docsToDelete = sigs.docs;
      for (let i = 0; i < docsToDelete.length; i += 400) {
        const batch = writeBatch(db);
        docsToDelete.slice(i, i + 400).forEach(d => batch.delete(d.ref));
        await batch.commit();
      }
      await deleteDoc(doc(db, 'schools', activeSchoolId, 'parent_notice', notice.uid));
    } catch (err) {
      alert(t('parentNotice.deleteFailed', { message: err.message }));
    }
  };

  const exportOutstanding = (notice) => {
    if (!notice) return;
    const columns = ['notice', 'class', 'classNumber', 'sid', 'name', 'status', 'signedBy', 'signedAt'];
    const rows = (notice.targetSids || []).map(sid => {
      const s = studentBySid[sid] || { sid, name: sid, currentClass: '', classNumber: '' };
      // Either guardian may sign, so which of them did is part of the record rather than an
      // assumption -- the school's evidence is "this adult acknowledged it", not just "someone did".
      const sig = signatures.find(x => x.noticeId === notice.uid && x.sid === sid);
      return {
        notice: notice.title,
        class: s.currentClass, classNumber: s.classNumber,
        sid: s.sid, name: s.name,
        status: (signedByNotice[notice.uid] || new Set()).has(sid) ? 'Signed' : 'Outstanding',
        signedBy: sig ? (sig.guardianName || sig.guardianEmail || '') : '',
        signedAt: sig?.signedAt || '',
      };
    });
    const csv = Papa.unparse(rows.length ? rows : [Object.fromEntries(columns.map(c => [c, '']))], { columns });
    const blob = new Blob([new Uint8Array([0xEF, 0xBB, 0xBF]), csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `parent_notice_${notice.uid}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // --- Settings -------------------------------------------------------------


  const filteredStaff = useMemo(() => {
    const q = staffSearch.trim().toLowerCase();
    if (!q) return staff;
    return staff.filter(s => `${s.name} ${s.initial} ${s.email}`.toLowerCase().includes(q));
  }, [staff, staffSearch]);

  // --- Dialog behaviour -----------------------------------------------------

  useEffect(() => {
    if (!showIssue) return;
    if (isMobile) closeSidebar?.();
    const onKey = (e) => { if (e.key === 'Escape' && !issuing) setShowIssue(false); };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [showIssue, isMobile, closeSidebar, issuing]);

  // --- Render ---------------------------------------------------------------

  const cardStyle = {
    backgroundColor: 'var(--color-bg-card)',
    borderRadius: isMobile ? 0 : 'var(--radius-lg)',
    boxShadow: isMobile ? 'none' : 'var(--shadow-sm)',
    // The list below is a flex column inside a fixed-height scroller. Without this, every card is
    // a shrinkable flex item: expand one notice and the browser squeezes all the others down to
    // slivers to make the column fit, instead of letting the column scroll.
    flexShrink: 0,
  };

  // Students only ever see their own pending notices; the other three tabs read staff-only data and
  // would simply fail for them.
  // Staff only. Students and guardians have their own module (MyNotice) -- this one is not offered
  // to them at all, which is why nothing below needs a role branch any more.
  const tabs = [
        { id: 'outstanding', label: t('parentNotice.tabs.outstanding'), icon: ClipboardList },
        { id: 'notice', label: t('parentNotice.tabs.notice'), icon: FileText },
        { id: 'payment', label: t('parentNotice.tabs.payment'), icon: CreditCard },
    ...(isAdmin ? [{ id: 'settings', label: t('parentNotice.tabs.settings'), icon: SettingsIcon }] : []),
  ];

  // useState only consults its initialiser on the first render, which happens before roleKnown --
  // so a student who reloads starts on 'outstanding', a tab their own tab strip does not offer.
  // Snap to one they actually have once the role lands. Keyed on the joined ids rather than `tabs`
  // itself, which is a fresh array every render.
  const tabIds = tabs.map(tab => tab.id).join(',');
  useEffect(() => {
    const allowed = tabIds.split(',');
    if (!allowed.includes(activeTab)) setActiveTab(allowed[0]);
  }, [tabIds, activeTab]);

  const years = useMemo(() => yearOptions(academicYears), [academicYears]);

  // Open on the current school year, but only once there is something in it -- a filter that opens
  // on an empty screen reads as a broken page rather than as a filter. Set once: after that the
  // choice is the user's, and a late-arriving snapshot must not move it under them.
  useEffect(() => {
    if (yearPicked || !academicYears.length) return;
    if (!notices.length && !payments.length) return;
    setYearFilter(initialYear(academicYears, [
      { list: notices, dateField: 'issue_datetime' },
      { list: payments, dateField: 'created_datetime' },
    ]));
    setYearPicked(true);
  }, [academicYears, notices, payments, yearPicked]);

  const shownNotices = useMemo(() => filterRecords(notices, {
    term: search, year: yearFilter, years: academicYears,
    dateField: 'issue_datetime', textOf: noticeText,
  }), [notices, search, yearFilter, academicYears]);

  const shownPayments = useMemo(() => filterRecords(payments, {
    term: search, year: yearFilter, years: academicYears,
    dateField: 'created_datetime', textOf: paymentText,
  }), [payments, search, yearFilter, academicYears]);

  const clearFilters = () => { setSearch(''); setYearFilter(''); };

  // One control set, shown on both list tabs.
  const listFilters = (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', flex: '1 1 12rem', minWidth: 0, maxWidth: '20rem' }}>
        <Search size={15} style={{ color: 'var(--color-text-muted)', flexShrink: 0 }} />
        <input
          className="input-field" value={search} onChange={e => setSearch(e.target.value)}
          aria-label={t('parentNotice.searchAria')}
          style={{ padding: '0.4rem 0.6rem', fontSize: '0.85rem' }}
        />
        {search && (
          <button type="button" onClick={() => setSearch('')} aria-label={t('parentNotice.clearSearch')}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', display: 'flex', padding: '0.2rem' }}>
            <X size={15} />
          </button>
        )}
      </div>
      <select
        value={yearFilter} onChange={e => { setYearFilter(e.target.value); setYearPicked(true); }}
        style={{ padding: '0 0.6rem', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)', fontSize: '0.85rem', backgroundColor: 'var(--color-bg-card)', color: 'var(--color-text-main)', flexShrink: 0 }}
      >
        <option value="">{t('parentNotice.allSchoolYears')}</option>
        {years.map(y => <option key={y.yearId} value={y.yearId}>{y.yearName || y.yearId}</option>)}
      </select>
    </>
  );

  // An empty list has to say which filter emptied it, or it reads as "nothing was ever sent".
  const noMatch = (what, total) => (
    <div style={{ padding: '2.5rem 1.5rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>
      <b style={{ display: 'block', color: 'var(--color-text-main)', fontSize: '0.95rem', marginBottom: '0.25rem' }}>
        No {what} matches
      </b>
      {total} {what}{total === 1 ? '' : 's'} in total.
      {' '}
      <button type="button" onClick={clearFilters}
        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--color-primary)', textDecoration: 'underline', font: 'inherit' }}>
        {t('parentNotice.clearFilters')}
      </button>
    </div>
  );


  if (!settingsLoaded) {
    return (
      <div className="animate-fade-in new-theme" style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ color: 'var(--color-text-muted)' }}>{t('common.loading')}</span>
      </div>
    );
  }


  return (
    <div className="animate-fade-in new-theme" style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div className="theme-tabs-bar" style={{ minHeight: '66px' }}>
        <div className="theme-tabs-group">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`theme-tab ${activeTab === tab.id ? 'active' : ''}`} style={{ whiteSpace: 'nowrap' }}>
                <div className="theme-tab-icon"><Icon size={18} /></div>
                {tab.label}
              </button>
            );
          })}
        </div>

        <HeaderActionsPortal>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {activeTab === 'outstanding' && (
              <span style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>
                {notices.length} notice{notices.length === 1 ? '' : 's'}
              </span>
            )}
            {activeTab === 'notice' && (
              <>
                {listFilters}
                {canIssue && (
                  <button className="btn-primary" onClick={() => setShowIssue(true)} style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', width: 'auto', padding: '0 0.9rem', whiteSpace: 'nowrap' }}>
                    <Plus size={15} /> Issue Notice
                  </button>
                )}
              </>
            )}
            {activeTab === 'payment' && (
              <>
                {listFilters}
                {canIssue && (
                  <button className="btn-primary" onClick={() => setShowPay(true)} style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', width: 'auto', padding: '0 0.9rem', whiteSpace: 'nowrap' }}>
                    <Plus size={15} /> New Payment
                  </button>
                )}
              </>
            )}
          </div>
        </HeaderActionsPortal>
      </div>

      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: isMobile ? '0.75rem' : '0 1.5rem 1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>

        {/* ---------------- OUTSTANDING ---------------- */}
        {activeTab === 'outstanding' && (
          <>
            {notices.length === 0 ? (
              <div style={{ ...cardStyle, padding: '3rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>
                {t('parentNotice.noNoticesIssued')}
              </div>
            ) : notices.map(n => {
              const rows = outstandingFor(n);
              const targeted = (n.targetSids || []).length;
              const signed = targeted - rows.length;
              const open = expandedNotices.has(n.uid);
              // A notice still in approval has no recipient rows at all, so the signature counts
              // would read "everyone has signed" -- a green tick on a letter nobody has seen. The
              // counts only mean anything once it is out.
              const live = n.status === STATUS.APPROVED || !n.status;
              const done = live && rows.length === 0 && targeted > 0;
              return (
                <div key={n.uid} style={{ ...cardStyle, overflow: 'hidden' }}>
                  <button
                    onClick={() => toggleExpanded(n.uid)}
                    style={{
                      width: '100%', textAlign: 'left', border: 'none', cursor: 'pointer',
                      background: 'transparent', padding: isMobile ? '0.85rem' : '1rem 1.25rem',
                      display: 'flex', alignItems: 'center', gap: '0.6rem',
                    }}
                  >
                    {open ? <ChevronDown size={18} style={{ flexShrink: 0 }} /> : <ChevronRight size={18} style={{ flexShrink: 0 }} />}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: '700', fontSize: '0.95rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{n.title}</div>
                      <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', fontSize: '0.78rem', color: 'var(--color-text-muted)', marginTop: '0.15rem' }}>
                        <span>{formatWhen(n.issue_datetime)}</span>
                        <span>{targeted} targeted</span>
                        {live && (
                          <>
                            <span style={{ color: 'var(--color-success-text)' }}>{signed} signed</span>
                            <span style={{ color: rows.length ? 'var(--color-danger)' : 'var(--color-success-text)', fontWeight: '700' }}>
                              {rows.length} outstanding
                            </span>
                          </>
                        )}
                        {/* Only shown while a notice is still going through approval. An approved
                            notice showing "Approved" for the rest of its life would just be noise on
                            every row. */}
                        {n.status && n.status !== STATUS.APPROVED && (
                          <span style={{ color: STATUS_COLOUR[n.status], fontWeight: 700 }}>
                            {statusWord(n.status)}
                          </span>
                        )}
                      </div>
                    </div>
                    {done && <CheckCircle2 size={18} color="var(--color-success)" style={{ flexShrink: 0 }} />}
                  </button>

                  {open && (
                    <div style={{ borderTop: '1px solid var(--color-border)' }}>
                      {/* The notice itself. Staff could see who had signed and who had not, but never
                          the words being signed -- the body only ever existed in the issue form and on
                          the family side. An approver deciding on a title alone is not approving
                          anything, and the office cannot answer a parent's question about a notice it
                          cannot read. Same three parts the family sees, so both sides read one text. */}
                      {(n.content || n.attachmentUrl || (n.eventDates || []).length > 0) && (
                        <div style={{ padding: isMobile ? '0.85rem' : '1rem 1.25rem', display: 'flex', flexDirection: 'column', gap: '0.7rem' }}>
                          {n.content && (
                            <div style={{ fontSize: '0.9rem', lineHeight: 1.65, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                              {n.content}
                            </div>
                          )}

                          {/* One notice can cover several days; they are listed rather than left in the
                              wording, because a slip that covers five days without naming them is
                              signed blind. */}
                          {Array.isArray(n.eventDates) && n.eventDates.length > 0 && (
                            <div style={{ padding: '0.55rem 0.7rem', borderRadius: 'var(--radius-md)', backgroundColor: 'var(--color-bg-app)', border: '1px solid var(--color-border)' }}>
                              <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', marginBottom: '0.3rem' }}>
                                {n.eventDates.length === 1 ? t('parentNotice.date') : t('parentNotice.datesCoveredOnce', { count: n.eventDates.length })}
                              </div>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem' }}>
                                {[...n.eventDates].sort().map(d => (
                                  <span key={d} style={{ fontSize: '0.8rem', padding: '0.15rem 0.45rem', borderRadius: 'var(--radius-md)', backgroundColor: 'var(--color-bg-card)', border: '1px solid var(--color-border)' }}>
                                    {d}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}

                          {n.attachmentUrl && (
                            <a href={n.attachmentUrl} target="_blank" rel="noreferrer" style={{ color: 'var(--color-primary)', display: 'inline-flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.85rem', alignSelf: 'flex-start' }}>
                              <Paperclip size={14} /> {n.attachmentName || t('parentNotice.attachment')}
                            </a>
                          )}
                        </div>
                      )}

                      {/* The decision belongs to whoever is named for the stage this notice is
                          currently at -- being named for the final stage does not let you skip the
                          first one, because skipping it would mean the unit never saw it. */}
                      {(() => {
                        const atFirst = n.status === STATUS.PENDING_FIRST;
                        const atFinal = n.status === STATUS.PENDING_FINAL;
                        const mine = (atFirst && myStages.first) || (atFinal && myStages.final) || isAdmin;
                        if (!(atFirst || atFinal) || !mine) return null;
                        return (
                          <div style={{ padding: '0.6rem 0.75rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center', backgroundColor: 'var(--color-bg-app)' }}>
                            <span style={{ fontSize: '0.82rem', color: 'var(--color-text-muted)', flex: 1 }}>
                              {atFinal
                                ? t('parentNotice.approveSendsToFamilies')
                                : t('parentNotice.approvePassesOn')}
                            </span>
                            <button className="btn-outline" onClick={() => decideNotice(n, 'reject')}
                                    style={{ width: 'auto', padding: '0.3rem 0.75rem', fontSize: '0.8rem' }}>
                              {t('parentNotice.sendBack')}
                            </button>
                            <button className="btn-primary" onClick={() => decideNotice(n, 'approve')}
                                    style={{ width: 'auto', padding: '0.3rem 0.75rem', fontSize: '0.8rem' }}>
                              {t('parentNotice.approve')}
                            </button>
                          </div>
                        );
                      })()}

                      {/* What happened to it, in order. Edits after approval appear here too: the
                          school office may fix a typo without sending it back, and that convenience
                          is only honest if the record shows the text changed after sign-off. */}
                      {(n.approvalLog || []).length > 0 && (
                        <div style={{ padding: '0.6rem 0.75rem', fontSize: '0.78rem', color: 'var(--color-text-muted)', lineHeight: 1.8, borderTop: '1px solid var(--color-border)' }}>
                          {n.approvalLog.map((entry, i) => (
                            <div key={i}>
                              <strong style={{ color: 'var(--color-text-main)' }}>{entry.action}</strong>
                              {entry.stage ? ` (${entry.stage})` : ''} — {entry.byName || entry.byEmail}
                              {' · '}{formatWhen(entry.at)}
                              {entry.note ? ` — “${entry.note}”` : ''}
                            </div>
                          ))}
                        </div>
                      )}

                      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', padding: '0.6rem 0.75rem', flexWrap: 'wrap' }}>
                        {canEdit(n) && (
                          <button className="btn-secondary" onClick={() => openEdit(n)} style={{ width: 'auto', padding: '0.3rem 0.75rem', fontSize: '0.8rem' }}>
                            <Pencil size={14} /> Edit
                          </button>
                        )}
                        {/* Nothing to export or sign off before the notice is out -- the buttons
                            would act on a recipient list that does not exist yet. */}
                        {live && (
                          <button className="btn-secondary" onClick={() => exportOutstanding(n)} style={{ width: 'auto', padding: '0.3rem 0.75rem', fontSize: '0.8rem' }}>
                            <Download size={14} /> Export
                          </button>
                        )}
                        {live && rows.length > 0 && (
                          <button className="btn-secondary" onClick={() => markAllSigned(n)} style={{ width: 'auto', padding: '0.3rem 0.75rem', fontSize: '0.8rem' }}>
                            <CheckCircle2 size={14} /> Mark all signed
                          </button>
                        )}
                      </div>

                      {!live ? (
                        <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>
                          {n.status === STATUS.REJECTED
                            ? t('parentNotice.sentBackNote')
                            : t('parentNotice.waitingApproval')}
                        </div>
                      ) : (
                        <>
                          {/* Kept, but no longer instead of the list: "everyone has signed" is worth
                              saying, and it is also the moment someone realises they ticked the
                              wrong name and needs the list to put it back. */}
                          {rows.length === 0 && (
                            <div style={{ padding: '1.25rem', textAlign: 'center', color: 'var(--color-success-text)', fontWeight: '600' }}>
                              {t('parentNotice.everyoneSigned')}
                            </div>
                          )}
                          <StudentRoster
                            rows={rosterFor(n)}
                            levelFor={levelFor}
                            levelLabel={levelLabel}
                            actionLabel={t('parentNotice.markSigned')}
                            undoLabel={t('parentNotice.unmark')}
                            doneLabel={t('parentNotice.signed')}
                            pendingLabel={t('parentNotice.outstandingTag')}
                            onAction={st => toggleSigned(n, st)}
                          />
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </>
        )}

        {activeTab === 'payment' && (
          <>
            {!canIssue && (
              <div style={{ ...cardStyle, padding: '2rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>
                <Lock size={32} style={{ opacity: 0.25, marginBottom: '0.75rem' }} />
                <div>{t('parentNotice.notAuthorisedPayment')}</div>
              </div>
            )}

            {payments.length === 0 ? (
              <div style={{ ...cardStyle, padding: '3rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>
                {t('parentNotice.noPaymentsYet')}
              </div>
            ) : shownPayments.length === 0 ? (
              <div style={cardStyle}>{noMatch('payment', payments.length)}</div>
            ) : shownPayments.map(pmt => {
              const unpaid = unpaidFor(pmt);
              const targeted = (pmt.targetSids || []).length;
              const paid = targeted - unpaid.length;
              const open = expandedPayments.has(pmt.uid);
              return (
                <div key={pmt.uid} style={{ ...cardStyle, overflow: 'hidden' }}>
                  <button
                    onClick={() => togglePaymentExpanded(pmt.uid)}
                    style={{
                      width: '100%', textAlign: 'left', border: 'none', cursor: 'pointer',
                      background: 'transparent', padding: isMobile ? '0.85rem' : '1rem 1.25rem',
                      display: 'flex', alignItems: 'center', gap: '0.6rem',
                    }}
                  >
                    {open ? <ChevronDown size={18} style={{ flexShrink: 0 }} /> : <ChevronRight size={18} style={{ flexShrink: 0 }} />}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: '700', fontSize: '0.95rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {pmt.title} <span style={{ color: 'var(--color-primary)' }}>${money(pmt.amountCents)}</span>
                      </div>
                      <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', fontSize: '0.78rem', color: 'var(--color-text-muted)', marginTop: '0.15rem' }}>
                        {pmt.deadline && <span>Due {pmt.deadline}</span>}
                        <span>{targeted} assigned</span>
                        <span style={{ color: 'var(--color-success-text)' }}>{paid} paid</span>
                        <span style={{ color: unpaid.length ? 'var(--color-danger)' : 'var(--color-success-text)', fontWeight: '700' }}>
                          {unpaid.length} unpaid
                        </span>
                        <span style={{ fontWeight: '700' }}>${money((pmt.amountCents || 0) * paid)} collected</span>
                      </div>
                    </div>
                    {unpaid.length === 0 && targeted > 0 && <CheckCircle2 size={18} color="var(--color-success)" style={{ flexShrink: 0 }} />}
                  </button>

                  {open && (
                    <div style={{ borderTop: '1px solid var(--color-border)' }}>
                      {/* Same omission as the notice body had: what the money is for was only ever
                          visible in the form that created it. */}
                      {pmt.description && (
                        <div style={{ padding: isMobile ? '0.85rem' : '1rem 1.25rem', fontSize: '0.9rem', lineHeight: 1.65, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                          {pmt.description}
                        </div>
                      )}
                      {canIssue && (
                        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', padding: '0.6rem 0.75rem' }}>
                          <button onClick={() => handleDeletePayment(pmt)} title={t('common.delete')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-danger)', padding: '0.25rem' }}>
                            <Trash2 size={16} />
                          </button>
                        </div>
                      )}
                      <div style={{ padding: '0 0.75rem 0.6rem', fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
                        {t('parentNotice.paymentsByKpay')}
                        "Record manually" is for exceptions only and is flagged as such in the record.
                      </div>
                      {unpaid.length === 0 && (
                        <div style={{ padding: '1.25rem', textAlign: 'center', color: 'var(--color-success-text)', fontWeight: '600' }}>
                          {t('parentNotice.everyonePaid')}
                        </div>
                      )}
                      <StudentRoster
                        rows={paymentRosterFor(pmt)}
                        levelFor={levelFor}
                        levelLabel={levelLabel}
                        actionLabel={t('parentNotice.recordManually')}
                        actionTitle={t('parentNotice.recordManuallyTitle')}
                        undoLabel={t('parentNotice.remove')}
                        doneLabel={t('parentNotice.paid')}
                        pendingLabel={t('parentNotice.unpaid')}
                        onAction={st => markPaid(pmt, st)}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </>
        )}

        {activeTab === 'notice' && (
          <>
            {!canIssue && (
              <div style={{ ...cardStyle, padding: '2rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>
                <Lock size={32} style={{ opacity: 0.25, marginBottom: '0.75rem' }} />
                <div>{t('parentNotice.notAuthorisedNotice')}</div>
              </div>
            )}

            <div style={cardStyle}>
              {notices.length === 0 ? (
                <div style={{ padding: '2.5rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>{t('parentNotice.noNoticesYet')}</div>
              ) : shownNotices.length === 0 ? (
                noMatch('notice', notices.length)
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                  <thead>
                    <tr style={headRow}>
                      <th style={th}>{t('parentNotice.issued')}</th>
                      <th style={th}>{t('parentNotice.title')}</th>
                      <th style={th}>{t('parentNotice.issuer')}</th>
                      <th style={th}>{t('parentNotice.targets')}</th>
                      <th style={th}>{t('parentNotice.attachment')}</th>
                      {canIssue && <th style={{ ...th, textAlign: 'right' }}></th>}
                    </tr>
                  </thead>
                  <tbody>
                    {shownNotices.map(n => (
                      <tr key={n.uid}>
                        <td style={{ ...td, color: 'var(--color-text-muted)' }}>{formatWhen(n.issue_datetime)}</td>
                        <td style={{ ...td, fontWeight: '600', whiteSpace: 'normal' }}>{n.title}</td>
                        <td style={td}>{n.issuerId || '—'}</td>
                        <td style={td}>{(n.targetSids || []).length}</td>
                        <td style={td}>
                          {n.attachmentUrl
                            ? <a href={n.attachmentUrl} target="_blank" rel="noreferrer" style={{ color: 'var(--color-primary)', display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}><Paperclip size={14} />{n.attachmentName}</a>
                            : '—'}
                        </td>
                        {canIssue && (
                          <td style={{ ...td, textAlign: 'right' }}>
                            <button onClick={() => handleDeleteNotice(n)} title={t('common.delete')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-danger)', padding: '0.25rem' }}>
                              <Trash2 size={16} />
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}

        {/* ---------------- SETTINGS ---------------- */}
        {activeTab === 'settings' && (isAdmin || isNoticeModuleAdmin) && (
          <>
            <div style={{ ...cardStyle, padding: isMobile ? '0.75rem' : '1.25rem' }}>
              <NoticeApprovalSettings
                schoolId={activeSchoolId}
                staff={staff}
                canManage={isAdmin || isNoticeModuleAdmin}
                isMobile={isMobile}
              />
            </div>

            <div style={{ ...cardStyle, padding: isMobile ? '0.75rem' : '1.25rem' }}>
              <NoticeTemplates
                schoolId={activeSchoolId}
                canManage={isAdmin || isNoticeModuleAdmin}
                isMobile={isMobile}
              />
            </div>

            <div style={{ flexShrink: 0 }}>
              <h2 style={{ fontSize: '1.25rem', fontWeight: '700', margin: 0 }}>{t('parentNotice.whoCanIssue')}</h2>
              <p style={{ color: 'var(--color-text-muted)', fontSize: '0.85rem', margin: '0.35rem 0 0' }}>
                {t('parentNotice.whoCanIssueIntro')}
              </p>
            </div>

            <div style={{ ...cardStyle, padding: isMobile ? '0.75rem' : '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                <ShieldCheck size={16} color="var(--color-primary)" />
                <strong style={{ fontSize: '0.9rem' }}>{t('parentNotice.authorisedStaff', { count: authorizedStaff.length })}</strong>
                <span style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)' }}>
                  {authorizedStaff.length === 0
                    ? t('parentNotice.onlyAdmins')
                    : staff.filter(s => authorizedStaff.includes(s.email)).map(s => s.initial).join(', ')}
                </span>
              </div>
              <p style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', margin: 0, lineHeight: 1.6 }}>
                {t('parentNotice.referenceOnly')}
              </p>
            </div>

            <div>
              <h2 style={{ fontSize: '1.25rem', fontWeight: '700', margin: '0.5rem 0 0' }}>{t('parentNotice.maintenance')}</h2>
              <p style={{ color: 'var(--color-text-muted)', fontSize: '0.85rem', margin: '0.35rem 0 0' }}>
                {t('parentNotice.maintenanceIntro')}
              </p>
            </div>

            <div style={{ ...cardStyle, padding: isMobile ? '0.75rem' : '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.75rem', alignItems: 'flex-start' }}>
              <div style={{ fontSize: '0.875rem' }}>
                {staleAudienceDocs.length === 0
                  ? t('parentNotice.listsUpToDate')
                  : t('parentNotice.listsStale', { count: staleAudienceDocs.length })}
              </div>
              <button
                className="btn-secondary"
                onClick={handleBackfillAudience}
                disabled={backfilling || staleAudienceDocs.length === 0}
                style={{ width: 'auto', padding: '0.55rem 1.1rem' }}
              >
                {backfilling ? t('parentNotice.repairing') : t('parentNotice.repairLists')}
              </button>
            </div>
          </>
        )}
      </div>

      {showPay && (
        <div onClick={() => { if (!creatingPayment) setShowPay(false); }} style={{ position: 'fixed', inset: 0, zIndex: 120, backgroundColor: 'var(--color-overlay)', display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center', padding: isMobile ? 0 : '2rem' }}>
          <div onClick={e => e.stopPropagation()} style={{ backgroundColor: 'var(--color-bg-card)', borderRadius: isMobile ? 'var(--radius-lg) var(--radius-lg) 0 0' : 'var(--radius-lg)', paddingBottom: 'env(safe-area-inset-bottom, 0px)', boxShadow: 'var(--shadow-lg)', width: '100%', maxWidth: isMobile ? '100%' : '880px', maxHeight: isMobile ? '92vh' : '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: isMobile ? '0.9rem 1rem' : '1.25rem 1.5rem', borderBottom: '1px solid var(--color-border)', flexShrink: 0 }}>
              <h3 style={{ fontSize: '1.1rem', fontWeight: '700', margin: 0 }}>{t('parentNotice.newPayment')}</h3>
              <button type="button" onClick={() => setShowPay(false)} aria-label={t('common.close')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', display: 'flex', padding: '0.25rem' }}><X size={20} /></button>
            </div>

            <form onSubmit={handleCreatePayment} style={{ padding: isMobile ? '1rem' : '1.5rem', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.9rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', marginBottom: '0.4rem' }}>{t('parentNotice.item')}</label>
                <input className="input-field" value={payForm.title} onChange={e => setPayForm({ ...payForm, title: e.target.value })} placeholder={t('parentNotice.itemPlaceholder')} required style={{ margin: 0 }} />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', marginBottom: '0.4rem' }}>{t('parentNotice.description')}</label>
                <textarea className="input-field" value={payForm.description} onChange={e => setPayForm({ ...payForm, description: e.target.value })} rows={3} style={{ margin: 0, resize: 'vertical', fontFamily: 'inherit' }} />
              </div>

              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 160px' }}>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', marginBottom: '0.4rem' }}>{t('parentNotice.amountHkd')}</label>
                  <input type="number" step="0.01" min="0.01" className="input-field" value={payForm.amount} onChange={e => setPayForm({ ...payForm, amount: e.target.value })} required style={{ margin: 0 }} />
                </div>
                <div style={{ flex: '1 1 160px' }}>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', marginBottom: '0.4rem' }}>{t('parentNotice.deadline')}</label>
                  <input type="date" className="input-field" value={payForm.deadline} onChange={e => setPayForm({ ...payForm, deadline: e.target.value })} style={{ margin: 0 }} />
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', marginBottom: '0.4rem' }}>Assign to ({payTargets.length})</label>
                <AudiencePicker
                  students={students}
                  classOptions={classOptions}
                  levelFor={levelFor}
                  levelLabel={levelLabel}
                  value={payTargets}
                  onChange={setPayTargets}
                  disabled={creatingPayment}
                />
              </div>

              {payTargets.length > 0 && toCents(payForm.amount) > 0 && (
                <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>
                  Total if everyone pays: <strong>${money(toCents(payForm.amount) * payTargets.length)}</strong>
                </p>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                <button type="button" className="btn-outline" onClick={() => setShowPay(false)} disabled={creatingPayment} style={{ width: 'auto', padding: '0.55rem 1.1rem' }}>{t('common.cancel')}</button>
                <button type="submit" className="btn-primary" disabled={creatingPayment || payTargets.length === 0} style={{ width: 'auto', padding: '0.55rem 1.1rem' }}>
                  {creatingPayment ? t('parentNotice.creating') : t('parentNotice.createPayment')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ---------------- ISSUE DIALOG ---------------- */}
      {showIssue && (
        <div onClick={() => { if (!issuing) closeForm(); }} style={{ position: 'fixed', inset: 0, zIndex: 120, backgroundColor: 'var(--color-overlay)', display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center', padding: isMobile ? 0 : '2rem' }}>
          <div onClick={e => e.stopPropagation()} style={{ backgroundColor: 'var(--color-bg-card)', borderRadius: isMobile ? 'var(--radius-lg) var(--radius-lg) 0 0' : 'var(--radius-lg)', paddingBottom: 'env(safe-area-inset-bottom, 0px)', boxShadow: 'var(--shadow-lg)', width: '100%', maxWidth: isMobile ? '100%' : '880px', maxHeight: isMobile ? '92vh' : '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: isMobile ? '0.9rem 1rem' : '1.25rem 1.5rem', borderBottom: '1px solid var(--color-border)', flexShrink: 0 }}>
              <h3 style={{ fontSize: '1.1rem', fontWeight: '700', margin: 0 }}>
                {editing ? t('parentNotice.editNotice') : t('parentNotice.issueNotice')}
              </h3>
              <button type="button" onClick={closeForm} aria-label={t('common.close')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', display: 'flex', padding: '0.25rem' }}><X size={20} /></button>
            </div>

            <form onSubmit={handleIssue} style={{ padding: isMobile ? '1rem' : '1.5rem', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.9rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', marginBottom: '0.4rem' }}>{t('parentNotice.noticeTitle')}</label>
                <input className="input-field" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required style={{ margin: 0 }} />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', marginBottom: '0.4rem' }}>{t('parentNotice.content')}</label>
                <textarea className="input-field" value={form.content} onChange={e => setForm({ ...form, content: e.target.value })} rows={5} required style={{ margin: 0, resize: 'vertical', fontFamily: 'inherit' }} />
                {templates.length > 0 && (
                  <div style={{ marginTop: '0.5rem' }}>
                    <span style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)' }}>
                      {t('parentNotice.startFromTemplate')}
                    </span>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem', marginTop: '0.3rem' }}>
                      {templates
                        .filter(tpl => (tpl.appliesTo || 'both') !== 'ole')
                        .map(tpl => (
                          <button
                            key={tpl.id}
                            type="button"
                            onClick={() => {
                              // Replaces what is in the box. Warned rather than silently merged --
                              // half a template and half something else reads like neither.
                              if (form.content.trim() &&
                                  !window.confirm(t('parentNotice.replaceWithTemplate'))) return;
                              const values = noticeValues({
                                schoolName,
                                title: form.title,
                                // A notice from this screen carries no dates, so a template's
                                // {{dates}} placeholder resolves to nothing rather than to stale text.
                                dates: [],
                              });
                              setForm(prev => ({
                                ...prev,
                                title: prev.title || fillTemplate(tpl.titlePattern, values),
                                content: fillTemplate(tpl.body, values),
                              }));
                            }}
                            style={{ fontSize: '0.78rem', padding: '0.2rem 0.55rem', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)', backgroundColor: 'transparent', cursor: 'pointer' }}
                          >
                            {tpl.name}
                          </button>
                        ))}
                    </div>
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 220px' }}>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', marginBottom: '0.4rem' }}>{t('parentNotice.issuedInNameOf')}</label>
                  <select className="input-field" value={form.issuerId} onChange={e => setForm({ ...form, issuerId: e.target.value })} required style={{ margin: 0 }}>
                    <option value="">{t('parentNotice.selectIssuer')}</option>
                    {staff.map(s => <option key={s.email} value={s.email}>{s.label}</option>)}
                  </select>
                </div>
                <div style={{ flex: '1 1 220px' }}>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', marginBottom: '0.4rem' }}>{t('parentNotice.attachmentOptional')}</label>
                  <input type="file" onChange={e => setFile(e.target.files?.[0] || null)} className="input-field" style={{ margin: 0, padding: '0.4rem 0.5rem' }} />
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', marginBottom: '0.4rem' }}>Target audience ({targets.length})</label>
                <AudiencePicker
                  students={students}
                  classOptions={classOptions}
                  levelFor={levelFor}
                  levelLabel={levelLabel}
                  value={targets}
                  onChange={setTargets}
                  disabled={issuing}
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                <button type="button" className="btn-outline" onClick={closeForm} disabled={issuing} style={{ width: 'auto', padding: '0.55rem 1.1rem' }}>{t('common.cancel')}</button>
                <button type="submit" className="btn-primary" disabled={issuing || targets.length === 0} style={{ width: 'auto', padding: '0.55rem 1.1rem', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                  <Send size={15} />
                  {issuing
                    ? 'Saving…'
                    : (editing ? t('common.save') : t('parentNotice.submitForApproval'))}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ParentNotice;
